import { parseISO, isValid } from 'date-fns';
const formatMap: Record<string, Intl.DateTimeFormatOptions> = {
  // Basic date formats
  ddMMMyyyy: { day: 'numeric', month: 'short', year: 'numeric' }, // 28 Aug 2025
  ddMMMyy: { day: 'numeric', month: 'short', year: '2-digit' }, // 28 Aug 25
  ddMMM: { day: 'numeric', month: 'short' }, // 28 Aug
  MMMdd: { month: 'short', day: 'numeric' }, // Aug 28
  'MMM dd': { month: 'short', day: '2-digit' }, // Aug 28 (with leading zero)
  MMMddyyyy: { month: 'short', day: 'numeric', year: 'numeric' }, // Aug 28 2025
  MMMyyyy: { month: 'short', year: 'numeric' }, // Aug 2025
  MMMyy: { month: 'short', year: '2-digit' }, // Aug 25
  MMMM: { month: 'long' }, // August
  MMM: { month: 'short' }, // Aug
  dd: { day: '2-digit' }, // 28
  yyyy: { year: 'numeric' }, // 2025

  // Standard date formats
  'MMMM dd, yyyy': { month: 'long', day: '2-digit', year: 'numeric' }, // August 28, 2025
  'MMM dd, yyyy': { month: 'short', day: '2-digit', year: 'numeric' }, // Aug 28, 2025
  'MM/dd/yyyy': { month: '2-digit', day: '2-digit', year: 'numeric' }, // 12/25/2025
  'MMMM yyyy': { month: 'long', year: 'numeric' }, // August 2025
  'dd MMMM yyyy': { day: 'numeric', month: 'long', year: 'numeric' }, // 28 August 2025

  'weekday, dd MMMM yyyy': { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, // Thursday, 28 August 2025
  'weekday\ndd MMMM yyyy': { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, // Thursday\n28 August 2025
  'weekday, MMMM d, yyyy': { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }, // Tuesday, August 28, 2025
  'weekday-short, MMMM dd': { weekday: 'short', month: 'long', day: '2-digit' }, // Tue, August 28
  'weekday-short, MMM do, h:mm': { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, // Tue, Oct 4, 4:00
  'weekday-short, MMM d': { weekday: 'short', month: 'short', day: 'numeric' }, // Thu, Jan 1
  'E, MMM dd, yyyy': { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' }, // Thu, Aug 28, 2025

  // Time formats
  'hh:mm': { hour: '2-digit', minute: '2-digit' }, // 04:00 PM or 16:00 based on the locale
  'yyyy-MM-dd': { year: 'numeric', month: '2-digit', day: '2-digit' }, // 2025-08-28
  'yyyy-MM-dd HH:mm': { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }, // 2025-08-28 16:00 or 2025-08-28 04:00 PM based on the locale

  // Date with time formats
  'MMMM do, yyyy': { month: 'long', day: 'numeric', year: 'numeric' }, // November 13th, 2025
  'MMMM do, yyyy h:mm': { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }, // November 13th, 2025 4:00pm or November 13th, 2025 16:00 based on the locale
} as const;

export type NullableDateInput = string | Date | number | null | undefined;
export type DateFormat = keyof typeof formatMap;
export type FormatDateOptions = Readonly<{ timezone?: string | null; titlecase?: boolean }>;

// Languages that use English-style ordinal suffixes (st, nd, rd, th) in dates
// Matches all English variants (en, en-US, en-GB, etc.) case-insensitively
const ENGLISH_LANGUAGE_REGEX = /^en(-.*)?$/i;

// Add ordinal suffix to day numbers (1st, 2nd, 3rd, 4th, etc.)
// Logic: Use modulo 10 to get the last digit (1→st, 2→nd, 3→rd), but exclude
// the special cases of 11th, 12th, and 13th which use 'th' instead of following
// the standard pattern. Modulo 100 helps identify these teen numbers.
// NOTE: Only applies to English and variants; other languages don't use ordinal suffixes in dates
const addOrdinalSuffix = (day: number, language: string): string => {
  // Only apply ordinal suffixes for languages that use them
  if (!ENGLISH_LANGUAGE_REGEX.test(language)) {
    return day.toString();
  }

  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) {
    return day + 'st';
  }
  if (j === 2 && k !== 12) {
    return day + 'nd';
  }
  if (j === 3 && k !== 13) {
    return day + 'rd';
  }
  return day + 'th';
};

// Format using Intl; optionally title-case weekday/month while preserving literals/order
const formatDate = (language: string, date: Date, options: Intl.DateTimeFormatOptions, titlecase: boolean): string => {
  if (!titlecase) {
    return new Intl.DateTimeFormat(language, options).format(date);
  }

  // NOTE: In French, Spanish and Italian, days/months are normally lowercase (e.g. "jeudi 4 juin 2026")
  // However, when displaying the date in a heading, we capitalize the weekday and month (e.g. "Jeudi 4 Juin 2026")
  // to improve visual hierarchy and readability.
  // This is not standard grammar but matches user expectations of dates in UI headings
  const parts = new Intl.DateTimeFormat(language, options).formatToParts(date);
  const capitalize = (value: string) => value.slice(0, 1).toLocaleUpperCase(language) + value.slice(1);

  return parts.map(({ type, value }) => (type === 'weekday' || type === 'month' ? capitalize(value) : value)).join('');
};

const formatMultilineDate = (language: string, date: Date, format: Intl.DateTimeFormatOptions, titlecase: boolean) => {
  const { timeZone, weekday, ...dateParts } = format;

  const weekdayOptions = { weekday: weekday ?? 'long', timeZone };
  const dateOptions = { ...dateParts, timeZone };

  const weekdayPart = formatDate(language, date, weekdayOptions, titlecase);
  const datePart = formatDate(language, date, dateOptions, titlecase);

  return `${weekdayPart}\n${datePart}`;
};

export const formatDateForLanguage = (language: string, input: NullableDateInput, format: DateFormat, options: FormatDateOptions = {}): string | undefined => {
  if (!input) return undefined;

  const date = input instanceof Date ? input : typeof input === 'number' ? new Date(input) : parseISO(input);
  if (!isValid(date)) return undefined;

  const { timezone, titlecase = false } = options;
  const formatOptions = {
    ...formatMap[format],
    ...(timezone && { timeZone: timezone }),
  };

  if (format === 'weekday\ndd MMMM yyyy') {
    return formatMultilineDate(language, date, formatOptions, titlecase);
  }

  // Special handling for ordinal date formats
  const ordinalFormats: DateFormat[] = ['weekday-short, MMM do, h:mm', 'MMMM do, yyyy', 'MMMM do, yyyy h:mm'];
  if (ordinalFormats.includes(format)) {
    const parts = new Intl.DateTimeFormat(language, formatOptions).formatToParts(date);
    // Get the day from the formatted parts to respect timezone, fallback to date.getDate() if not found
    const dayPart = parts.find(part => part.type === 'day');
    const day = dayPart ? parseInt(dayPart.value, 10) : date.getDate();
    const dayWithOrdinal = addOrdinalSuffix(day, language);

    return parts
      .map(({ type, value }) => {
        if (type === 'day') {
          return dayWithOrdinal;
        }
        return value;
      })
      .join('');
  }

  // Preserve locale literals; optionally title-case only weekday/month parts
  return formatDate(language, date, formatOptions, titlecase);
};

export const getDateFormatterForLanguage = (language: string) => {
  return (input: NullableDateInput, format: DateFormat, options?: FormatDateOptions) => formatDateForLanguage(language, input, format, options);
};
